import { useContext, useEffect, useState } from 'react';
import { Box, Grid, Typography, Chip, Paper, Divider } from '@mui/material';
import {
    DateRange as DateRangeIcon,
    CalendarMonth as CalendarMonthIcon,
    Devices as DevicesIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttDialog,
    SttFormPeriod,
    SttFormTime,
    SttSelectWeekdays,
} from '../../sporttia/all';
import { SttSelectableChips } from '../../sporttia/SttSelectableChips';
import { showErrors } from '../../lib/utils';
import { Device } from '../../types/models/DeviceSpace';
import { PermissionRequirement } from '../../lib/permissions';

type DeviceAuth = {
    id?: number;
    dateini: string;
    dateend: string;
    timeini: string;
    timeend: string;
    weekdays: number[];
    devices?: Device[];
    [key: string]: unknown;
};

type DeviceAuthDialogProps = {
    open: boolean;
    item: DeviceAuth;
    onSave?: () => void;
    onClose?: () => void;
    /** Permiso v8 para guardar y borrar. Sin él, se pueden siempre. */
    permission?: PermissionRequirement;
};

type FormChangeEvent = {
    name: string | undefined;
    value: unknown;
};

type DeviceAuthResponse = {
    deviceAuth?: DeviceAuth;
};

type DevicesListResponse = {
    rows?: Device[];
};

type ErrorWrapper = {
    error: unknown;
};

/**
 * DeviceAuthDialog : Dialog to add device authorizations to groups, activities, whatever
 * Depending on the <item> spec, the API will respond differently, for ex: idGroup to authorize a group, idEvent for an event, etc.
 */
export default function DeviceAuthDialog({
    open,
    item,
    onSave,
    onClose,
    permission,
}: DeviceAuthDialogProps): React.ReactElement {
    const [auth, setAuth] = useState<DeviceAuth>(item);
    const [devices, setDevices] = useState<Device[]>([]);
    const [deviceIds, setDeviceIds] = useState<string[]>([]);

    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();

    // Load devices from API
    function loadDevices(): void {
        if (!cxt?.sc?.id) return;

        cxt.api('GET', `/scs/${cxt.sc?.id}/devices`, {
            params: {},
            success: (result: DevicesListResponse) => {
                if (result.rows) {
                    setDevices(result.rows);
                    if (auth.id && auth.devices) {
                        setDeviceIds(
                            auth.devices.map((device: any) =>
                                String(device.id),
                            ),
                        );
                    }
                }
            },
        });
    }

    // Form fields changed
    function handleChange({ name, value }: FormChangeEvent): void {
        if (!name) return;
        setAuth({ ...auth, [name]: value });
    }

    // Add devices to an authorization
    function addDevicesToAuth(authId: number): void {
        if (deviceIds.length === 0) {
            saveAndClose();
            return;
        }

        cxt?.api('POST', `/devices/auths/${authId}/devices`, {
            params: {
                deviceIds: deviceIds.map((id) => Number(id)),
            },
            success: () => {
                saveAndClose();
            },
            error: (errorWrapper: unknown) => {
                const err = errorWrapper as ErrorWrapper;
                if (err?.error && cxt?.showMessage) {
                    showErrors(
                        err.error as Parameters<typeof showErrors>[0],
                        cxt.showMessage as (
                            type: string,
                            message: string,
                        ) => void,
                    );
                }
            },
        });
    }

    // Convenience function to call the save and close callbacks at once
    function saveAndClose(): void {
        if (typeof onSave === 'function') {
            onSave();
        }

        if (typeof onClose === 'function') {
            onClose();
        }
    }

    // Create new authorization, then add devices
    function createAuth(): void {
        // Exclude devices from params - they are added via separate API call
        const { devices: _devices, ...authParams } = auth;

        cxt?.api('POST', `/devices/auths`, {
            params: {
                ...authParams,
            },
            success: (response: DeviceAuthResponse) => {
                if (response.deviceAuth?.id) {
                    addDevicesToAuth(response.deviceAuth.id);
                }
            },
            error: (errorWrapper: unknown) => {
                const err = errorWrapper as ErrorWrapper;
                if (err?.error && cxt?.showMessage) {
                    showErrors(
                        err.error as Parameters<typeof showErrors>[0],
                        cxt.showMessage as (
                            type: string,
                            message: string,
                        ) => void,
                    );
                }
            },
        });
    }

    // Update existing authorization
    function updateAuth(): void {
        if (!auth.id) return;

        cxt?.api('PUT', `/devices/auths/${auth.id}/devices`, {
            params: {
                dateini: auth.dateini,
                dateend: auth.dateend,
                timeini: auth.timeini,
                timeend: auth.timeend,
                weekdays: auth.weekdays,
                devices: deviceIds.map((id: any) => Number(id)),
            },
            success: (response: DeviceAuthResponse) => {
                if (response.deviceAuth) {
                    saveAndClose();
                }
            },
            error: (errorWrapper: unknown) => {
                const err = errorWrapper as ErrorWrapper;
                if (err?.error && cxt?.showMessage) {
                    showErrors(
                        err.error as Parameters<typeof showErrors>[0],
                        cxt.showMessage as (
                            type: string,
                            message: string,
                        ) => void,
                    );
                }
            },
        });
    }

    // create or update depending on whether we have an id or not
    function save(): void {
        if (auth.id) {
            updateAuth();
        } else {
            createAuth();
        }
    }

    // Delete this auth
    function deleteAuth(): void {
        if (!auth.id) {
            return;
        }

        cxt?.api('DELETE', `/devices/auths/${auth.id}`, {
            confirmation: true,
            success: (result: DeviceAuthResponse) => {
                if (result.deviceAuth) {
                    saveAndClose();
                }
            },
        });
    }

    const buttons: Array<{
        type: 'delete' | 'save';
        onClick: () => void;
        permission?: PermissionRequirement;
    }> = [];

    if (auth.id) {
        buttons.push({
            type: 'delete' as const,
            onClick: deleteAuth,
            permission,
        });
    }

    buttons.push({
        type: 'save' as const,
        onClick: save,
        permission,
    });

    // Load available devices
    useEffect(() => {
        loadDevices();
    }, []);

    // Section header component
    const SectionHeader = ({
        icon,
        title,
    }: {
        icon: React.ReactNode;
        title: string;
    }) => (
        <Box display="flex" alignItems="center" gap={1} mb={2}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 32,
                    height: 32,
                    borderRadius: 1,
                    backgroundColor: 'primary.lighter',
                    color: 'var(--brand-text)',
                }}
            >
                {icon}
            </Box>
            <Typography variant="subtitle1" fontWeight={600}>
                {title}
            </Typography>
        </Box>
    );

    return (
        <SttDialog
            title={t('Horary')}
            open={open}
            onClose={onClose}
            content={
                <Box sx={{ minWidth: { sm: 500 } }}>
                    {/* Period and Time Section */}
                    <Paper
                        variant="outlined"
                        sx={{ p: 2, mb: 2, borderRadius: 2 }}
                    >
                        <SectionHeader
                            icon={<DateRangeIcon fontSize="small" />}
                            title={t('generic.periodAndTime')}
                        />
                        <SttFormPeriod
                            nameIni="dateini"
                            nameEnd="dateend"
                            defValIni={auth.dateini}
                            defValEnd={auth.dateend}
                            onChange={handleChange}
                        />
                        <Box sx={{ mt: 2 }}>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <SttFormTime
                                        min="00:00:00"
                                        name="timeini"
                                        caption={t(
                                            'sc.class.schedules.hourInit',
                                        )}
                                        defVal={auth.timeini}
                                        interval={5}
                                        onChange={handleChange}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <SttFormTime
                                        min="00:00:00"
                                        name="timeend"
                                        caption={t(
                                            'sc.class.schedules.hourEnd',
                                        )}
                                        defVal={auth.timeend}
                                        interval={5}
                                        onChange={handleChange}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </Paper>

                    {/* Weekdays Section */}
                    <Paper
                        variant="outlined"
                        sx={{ p: 2, mb: 2, borderRadius: 2 }}
                    >
                        <SectionHeader
                            icon={<CalendarMonthIcon fontSize="small" />}
                            title={t('Weekdays')}
                        />
                        <SttSelectWeekdays
                            name="weekdays"
                            selected={auth.weekdays}
                            onChange={handleChange}
                            disabled={false}
                        />
                    </Paper>

                    {/* Devices Section */}
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                        <SectionHeader
                            icon={<DevicesIcon fontSize="small" />}
                            title={t('DevicesToAuth')}
                        />
                        {devices.length > 0 ? (
                            <SttSelectableChips
                                chipSize="medium"
                                ChipComponent={Chip}
                                name="deviceIds"
                                items={devices.map((device: any) => ({
                                    label: device.name,
                                    value: String(device.id),
                                }))}
                                selected={deviceIds}
                                onChange={({ value }: any) =>
                                    setDeviceIds(
                                        Array.isArray(value) ? value : [value],
                                    )
                                }
                            />
                        ) : (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontStyle: 'italic' }}
                            >
                                {t('generic.noResults')}
                            </Typography>
                        )}
                        {deviceIds.length > 0 && (
                            <Box mt={1}>
                                <Divider sx={{ my: 1 }} />
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    {t('generic.selected')}: {deviceIds.length}
                                </Typography>
                            </Box>
                        )}
                    </Paper>
                </Box>
            }
            buttons={buttons}
        />
    );
}
